import React from "react";
import { motion } from "framer-motion";
import StarField from "../components/StarField";
import DeepSpaceBackground from "../components/DeepSpaceBackground";
import SEO from "../components/SEO";

const fadeIn = {
  hidden: { opacity: 0, y: 15 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 1,
      ease: "easeOut",
    },
  }),
};

const Divider = () => (
  <div className="h-px w-16 sm:w-20 md:w-24 bg-white/15 my-6 sm:my-8 md:my-10" />
);

export const LandingPage = () => {
  return (
    <div
      data-testid="landing-page"
      className="relative min-h-screen bg-[#192742] overflow-hidden"
    >
      <SEO 
        title="SDRO — Scientific Development & Research Organisation | Eminova Felicis"
        description="A private circle of scientific inquiry for extraordinary young minds. SDRO mentors exceptional students in advanced astronomy, space science, and frontier space technologies."
        path="/"
      />
      {/* Background layers */}
      <DeepSpaceBackground />
      <StarField />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-12 md:py-24">
        <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
          
          {/* Logo */}
          <motion.div
            data-testid="main-logo"
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mb-3 md:mb-4 relative"
          >
            {/* Glow behind logo */}
            <div 
              className="absolute inset-0 blur-3xl opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(70, 130, 200, 0.5) 0%, transparent 70%)',
                transform: 'scale(1.3)',
              }}
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_1d6b5c61-8e78-4158-b22f-a4e0d0ebfc0c/artifacts/on9voaqw_App_icon.png"
              alt="SDRO - Scientific Development & Research Organisation"
              className="w-28 sm:w-36 md:w-44 lg:w-52 h-auto mx-auto relative z-10 rounded-lg"
              style={{
                filter: 'drop-shadow(0 0 30px rgba(70, 130, 200, 0.4))',
              }}
            />
          </motion.div>

          {/* Full form - Highlighted */}
          <motion.h1
            data-testid="full-form"
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.08em] md:tracking-[0.12em] text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-white to-blue-200 px-2"
            style={{
              textShadow: '0 0 40px rgba(147, 197, 253, 0.4)',
            }}
          >
            Scientific Development & Research Organisation
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            data-testid="subtitle"
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-6 md:mt-8 font-body text-sm sm:text-base md:text-lg font-light tracking-wide text-slate-400 italic px-2"
          >
            A private circle of scientific inquiry for extraordinary young minds.
          </motion.p>

          <motion.div
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Divider />
          </motion.div>

          {/* Main Copy */}
          <motion.p
            data-testid="main-copy"
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="font-body text-sm sm:text-base md:text-lg font-normal leading-relaxed text-slate-400/90 max-w-xl px-2"
          >
            SDRO is a carefully curated intellectual fellowship that mentors a
            small cohort of exceptional students in advanced astronomy, space
            science, and frontier space technologies. Every engagement is
            bespoke, research-driven, and guided by rigorous scientific thinking
            rather than mass education.
          </motion.p>

          <motion.div
            custom={5}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Divider />
          </motion.div>

          {/* Policy Statement */}
          <motion.div
            data-testid="policy-statement"
            custom={6}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="space-y-2 px-2"
          >
            <p className="font-body text-xs sm:text-sm md:text-base tracking-[0.06em] sm:tracking-[0.08em] uppercase text-slate-500">
              Access to SDRO is strictly by invitation.
            </p>
            <p className="font-body text-xs sm:text-sm md:text-base text-slate-500/70">
              We do not entertain open applications, or operate through
              conventional admissions.
            </p>
          </motion.div>

          <motion.div
            custom={7}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Divider />
          </motion.div>

          {/* Contact */}
          <motion.div
            data-testid="contact-section"
            custom={8}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-2 px-2"
          >
            <p className="font-body text-[10px] sm:text-xs md:text-sm tracking-[0.1em] sm:tracking-[0.12em] uppercase text-slate-600 mb-3 md:mb-4">
              If you have received a formal referral or wish to seek
              consideration, write to:
            </p>
            <a
              href="mailto:info@sdro.in"
              data-testid="contact-email"
              className="font-body text-base sm:text-lg md:text-xl text-white/90 hover:text-white transition-colors duration-300 tracking-widest"
            >
              info@sdro.in
            </a>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.footer
          data-testid="footer"
          custom={9}
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="w-full mt-12 md:mt-20 pt-6 md:pt-8 border-t border-white/10"
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <p className="font-body text-xs sm:text-sm text-slate-500 mb-6 md:mb-8 text-center">
              SDRO is an <span className="text-white/80">Eminova Felicis</span> Company
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Gurugram Office */}
              <div data-testid="gurugram-office" className="text-center md:text-right">
                <h4 className="font-body text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] uppercase text-slate-600 mb-1.5 md:mb-2">
                  Gurugram Office
                </h4>
                <p className="font-body text-xs sm:text-sm text-slate-500/80 leading-relaxed">
                  Eminova Felicis, 93, Sector 44,<br />
                  Gurugram, Haryana 122003
                </p>
              </div>

              {/* Bangalore Office */}
              <div data-testid="bangalore-office" className="text-center md:text-left">
                <h4 className="font-body text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] uppercase text-slate-600 mb-1.5 md:mb-2">
                  Bangalore Office
                </h4>
                <p className="font-body text-xs sm:text-sm text-slate-500/80 leading-relaxed">
                  SDRO, Oxford Towers, HAL Old Airport Rd,<br />
                  Kodihalli, 560076 Bengaluru KA, India
                </p>
              </div>
            </div>

            <p className="font-body text-[10px] sm:text-xs text-slate-700 mt-6 md:mt-8 pb-6 md:pb-8 text-center tracking-wide">
              © 2026 Eminova Felicis. All rights reserved.
            </p>
          </div>
        </motion.footer>
      </div>
    </div>
  );
};

export default LandingPage;
